import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  History,
  FileText,
  Phone,
  Mail,
  Users,
  MessageSquare,
  Calendar,
  Send,
  Linkedin,
  UserCheck,
  CalendarCheck,
  Megaphone,
} from "lucide-react";
import React from "react";
import { useAccountCampaignTimeline, type AccountCampaignEvent } from "@/hooks/useAccountCampaigns";

const EVENT_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  campaign_enrollment: { icon: <Megaphone className="h-3.5 w-3.5" />, color: "border-blue-500 text-blue-600" },
  outreach_sent: { icon: <Send className="h-3.5 w-3.5" />, color: "border-indigo-500 text-indigo-600" },
  outreach_replied: { icon: <MessageSquare className="h-3.5 w-3.5" />, color: "border-green-500 text-green-600" },
  connection_accepted: { icon: <UserCheck className="h-3.5 w-3.5" />, color: "border-emerald-500 text-emerald-600" },
  meeting_booked: { icon: <CalendarCheck className="h-3.5 w-3.5" />, color: "border-purple-500 text-purple-600" },
  pipeline_activity: { icon: <FileText className="h-3.5 w-3.5" />, color: "border-primary text-primary" },
  note: { icon: <FileText className="h-3.5 w-3.5" />, color: "border-primary text-primary" },
  call: { icon: <Phone className="h-3.5 w-3.5" />, color: "border-amber-500 text-amber-600" },
  email: { icon: <Mail className="h-3.5 w-3.5" />, color: "border-sky-500 text-sky-600" },
  meeting: { icon: <Users className="h-3.5 w-3.5" />, color: "border-violet-500 text-violet-600" },
  demo: { icon: <MessageSquare className="h-3.5 w-3.5" />, color: "border-pink-500 text-pink-600" },
};

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  linkedin: <Linkedin className="h-3 w-3" />,
  email: <Mail className="h-3 w-3" />,
  emailbison: <Mail className="h-3 w-3" />,
};

interface AccountEngagementTabProps {
  accountId: string;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function AccountEngagementTab({ accountId }: AccountEngagementTabProps) {
  const { data: events, isLoading } = useAccountCampaignTimeline(accountId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!events?.length) {
    return (
      <div className="p-8 text-center">
        <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No Engagement History</h3>
        <p className="text-muted-foreground text-sm">
          Campaign enrollments, outreach messages, and pipeline activities will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Summary bar */}
      <div className="flex items-center gap-4 flex-wrap mb-4 text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">{events.length} events</span>
        <span>{events.filter((e) => e.event_type === "campaign_enrollment").length} enrollments</span>
        <span>{events.filter((e) => e.event_type === "outreach_sent").length} sends</span>
        <span>{events.filter((e) => e.event_type === "outreach_replied").length} replies</span>
        <span>{events.filter((e) => e.event_type === "meeting_booked").length} meetings</span>
        <span>{events.filter((e) => e.event_type === "pipeline_activity").length} pipeline</span>
      </div>

      {/* Timeline */}
      <div className="relative border-l-2 border-muted ml-4 space-y-4">
        {events.map((event: AccountCampaignEvent) => {
          const cfg = EVENT_CONFIG[event.status || event.event_type] || EVENT_CONFIG[event.event_type] || EVENT_CONFIG.pipeline_activity;
          return (
            <div key={event.id} className="relative pl-8">
              <div className={`absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-background border-2 flex items-center justify-center ${cfg.color}`}>
                {cfg.icon}
              </div>
              <div className="space-y-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{event.title}</span>
                  {event.platform && (
                    <span className="flex items-center gap-0.5 text-muted-foreground">
                      {PLATFORM_ICONS[event.platform]}
                    </span>
                  )}
                  {event.campaign_name && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1">
                      {event.campaign_name}
                    </Badge>
                  )}
                  {event.status && event.status !== event.event_type && (
                    <Badge variant="secondary" className="text-[10px] h-4 px-1">
                      {event.status.replace(/_/g, " ")}
                    </Badge>
                  )}
                </div>
                {event.contact_name && (
                  <p className="text-xs text-muted-foreground">{event.contact_name}</p>
                )}
                {event.detail && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{event.detail}</p>
                )}
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  {formatDate(event.event_date)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
